<section class="page">
  <header class="page-header">
    <div>
      <h1>Partner reward uploads</h1>
      <p>Upload aggregator CSVs to award shopping-partner cashback points</p>
    </div>
  </header>

  @if (error()) {
    <div class="alert alert-error">{{ error() }}</div>
  }
  @if (success()) {
    <div class="alert alert-success">{{ success() }}</div>
  }

  <div class="panel upload-panel">
    <h2>Upload CSV</h2>
    <div class="upload-form">
      <label>
        <span>Aggregator</span>
        <select [(ngModel)]="aggregator">
          @for (a of aggregators(); track a.key) {
            <option [value]="a.key">{{ a.label }}</option>
          }
        </select>
      </label>
      <label>
        <span>CSV file</span>
        <input type="file" accept=".csv,text/csv" (change)="onFileSelected($event)" />
      </label>
      <button
        class="btn btn-primary"
        type="button"
        (click)="upload()"
        [disabled]="!selectedFile || uploading()"
      >
        {{ uploading() ? 'Processing…' : 'Upload &amp; award' }}
      </button>
    </div>
    <p class="muted hint">
      IMPACT scaffold columns: user_id, partner_id or partner_name, amount, plan_key, external_reference, purchase_at.
      Rows are awarded independently — failures do not stop the batch.
    </p>
  </div>

  <div class="filters">
    <select [(ngModel)]="filterAggregator">
      <option value="">All aggregators</option>
      @for (a of aggregators(); track a.key) {
        <option [value]="a.key">{{ a.label }}</option>
      }
    </select>
    <select [(ngModel)]="filterStatus">
      <option value="">All statuses</option>
      <option value="processing">processing</option>
      <option value="completed">completed</option>
      <option value="completed_with_errors">completed_with_errors</option>
    </select>
    <button class="btn btn-secondary" type="button" (click)="load(1)" [disabled]="loading()">
      Search
    </button>
  </div>

  <div class="panel">
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Aggregator</th>
            <th>File</th>
            <th>Status</th>
            <th>Rows</th>
            <th>Provided</th>
            <th>Failed</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @if (loading()) {
            <tr><td colspan="9" class="muted">Loading…</td></tr>
          } @else if (!uploads().length) {
            <tr><td colspan="9" class="muted">No uploads yet</td></tr>
          } @else {
            @for (row of uploads(); track row.id) {
              <tr>
                <td>{{ row.id }}</td>
                <td>{{ row.aggregator }}</td>
                <td class="mono">{{ row.original_filename || '—' }}</td>
                <td>
                  <span class="badge" [attr.data-status]="row.status === 'completed' ? 'active' : 'pending'">
                    {{ row.status }}
                  </span>
                </td>
                <td>{{ row.total_rows }}</td>
                <td>{{ row.provided_count }}</td>
                <td>{{ row.failed_count }}</td>
                <td class="muted">{{ row.created_at | date: 'medium' }}</td>
                <td>
                  <button class="btn btn-secondary btn-sm" type="button" (click)="openDetail(row)">
                    View rows
                  </button>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
    @if (total() > limit) {
      <div class="pager">
        <span class="muted">Page {{ page() }} / {{ totalPages() }} ({{ total() }} total)</span>
        <div class="pager-actions">
          <button class="btn btn-secondary btn-sm" type="button" [disabled]="page() <= 1 || loading()" (click)="load(page() - 1)">
            Prev
          </button>
          <button class="btn btn-secondary btn-sm" type="button" [disabled]="page() >= totalPages() || loading()" (click)="load(page() + 1)">
            Next
          </button>
        </div>
      </div>
    }
  </div>

  @if (selectedUpload(); as upload) {
    <div class="panel detail-panel">
      <div class="detail-header">
        <div>
          <h2>Upload #{{ upload.id }} — {{ upload.aggregator }}</h2>
          <p class="muted">
            {{ upload.provided_count }} provided · {{ upload.failed_count }} failed · {{ upload.total_rows }} total
          </p>
        </div>
        <button class="btn btn-ghost" type="button" (click)="closeDetail()">Close</button>
      </div>
      <div class="filters">
        <select [(ngModel)]="detailStatus" (ngModelChange)="loadDetail(1)">
          <option value="">All row statuses</option>
          <option value="provided">provided</option>
          <option value="failed">failed</option>
          <option value="pending">pending</option>
        </select>
      </div>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>User</th>
              <th>Partner</th>
              <th>Amount</th>
              <th>Plan</th>
              <th>Points</th>
              <th>Status</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            @if (detailLoading()) {
              <tr><td colspan="8" class="muted">Loading…</td></tr>
            } @else if (!detailRows().length) {
              <tr><td colspan="8" class="muted">No rows</td></tr>
            } @else {
              @for (r of detailRows(); track r.id) {
                <tr>
                  <td>{{ r.row_number }}</td>
                  <td>{{ r.user_id || '—' }}</td>
                  <td>{{ r.shopping_partner_id || r.partner_external_key || '—' }}</td>
                  <td>{{ r.amount_aud != null ? ('$' + r.amount_aud) : '—' }}</td>
                  <td>{{ r.plan_key || '—' }}</td>
                  <td>{{ r.points_awarded != null ? r.points_awarded : '—' }}</td>
                  <td>
                    <span class="badge" [attr.data-status]="r.status === 'provided' ? 'active' : 'pending'">
                      {{ r.status }}
                    </span>
                  </td>
                  <td class="error-cell">{{ r.error_code || r.error_message || '—' }}</td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
      @if (detailTotal() > detailLimit) {
        <div class="pager">
          <span class="muted">Page {{ detailPage() }} / {{ detailTotalPages() }}</span>
          <div class="pager-actions">
            <button class="btn btn-secondary btn-sm" type="button" [disabled]="detailPage() <= 1 || detailLoading()" (click)="loadDetail(detailPage() - 1)">
              Prev
            </button>
            <button class="btn btn-secondary btn-sm" type="button" [disabled]="detailPage() >= detailTotalPages() || detailLoading()" (click)="loadDetail(detailPage() + 1)">
              Next
            </button>
          </div>
        </div>
      }
    </div>
  }
</section>
